import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Box, CssBaseline, useMediaQuery } from '@mui/material';

import { AuthenticatedTemplate } from '@azure/msal-react';

// project imports
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Sidebar from './components/Sidebar/Sidebar';
import MobileHeader from './components/MobileHeader/MobileHeader';
import MobileSidebar from './components/MobileSidebar/MobileSidebar';

import navigation from 'components/menu-items';
// import { drawerWidth } from 'redux/store/constant';
import { SET_MENU } from 'redux/store/actions';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    'margin',
    open
      ? {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      }
      : {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }
  ),

  [theme.breakpoints.up('xs')]: {
    marginRight: 0,
    // width: `calc(100% - ${drawerWidth}px)`
    width: `80%`
  },

  marginTop: 0,
  background: 'transparent' // Explicitly set the background to transparent or another color
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = ({ children }) => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };

  const prevMatchDownMd = useRef(matchDownMd);
  useEffect(() => {
    // Auto-manage drawer state when screen size changes between mobile and desktop
    if (prevMatchDownMd.current !== matchDownMd) {
      prevMatchDownMd.current = matchDownMd;

      if (!matchDownMd) {
        if (!leftDrawerOpened) {
          dispatch({ type: SET_MENU, opened: true });
        }
      } else {
        if (leftDrawerOpened) {
          dispatch({ type: SET_MENU, opened: false });
        }
      }
    }
  }, [matchDownMd, leftDrawerOpened, dispatch]);

  return (
    <>
      <AuthenticatedTemplate>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <CssBaseline />
          
          {/* Mobile Header - only show on mobile */}
          {matchDownMd && (
            <MobileHeader onMenuClick={handleLeftDrawerToggle} />
          )}
          
          <Box sx={{ display: 'flex', flex: 1 }}>
            {/* Desktop Sidebar */}
            {!matchDownMd && (
              <Sidebar drawerOpen={leftDrawerOpened} handleLeftDrawerToggle={handleLeftDrawerToggle} />
            )}

            {/* Mobile Sidebar */}
            {matchDownMd && (
              <MobileSidebar isOpen={leftDrawerOpened} onClose={handleLeftDrawerToggle} />
            )}

            <Main theme={theme} open={leftDrawerOpened} sx={{ padding: '0' }}>
              <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
              {children}
            </Main>
          </Box>
        </Box>
      </AuthenticatedTemplate>
    </>
  );
};

export default MainLayout;
